import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import itemsCatalog from '../data/itemsCatalog.json';
import PlayerAvatar from './PlayerAvatar';
import { copyToClipboard as copyToClipboardUtil } from '../utils/clipboard';

const ScrollableRow = ({ children }) => {
  const scrollRef = useRef(null);
  
  const scroll = (dir) => {
    if (scrollRef.current) {
      const scrollAmount = scrollRef.current.children[0]?.offsetWidth || 70;
      scrollRef.current.scrollBy({ left: dir * (scrollAmount + 10), behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        el.scrollBy({ left: e.deltaY > 0 ? 60 : -60, behavior: 'auto' });
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  return (
    <div className="scroll-wrapper">
      <button className="scroll-arrow left" onClick={() => scroll(-1)}>‹</button>
      <div className="scroll-content" ref={scrollRef}>
        {children}
      </div>
      <button className="scroll-arrow right" onClick={() => scroll(1)}>›</button>
    </div>
  );
};

// Определяет, ведёт ли ссылка на карточку товара внутри НАШЕГО же бота
// (такую ссылку формирует кнопка "поделиться" — https://t.me/<bot>?startapp=item_<id>).
// Используется, чтобы при клике на кнопку рекламного баннера с такой ссылкой
// открыть карточку товара сразу внутри приложения, а не через
// Telegram.WebApp.openLink — переход по ссылке в браузере закрывает текущее
// мини-приложение и открывает его заново с нуля (выглядит как перезагрузка).
function parseInternalItemLink(rawUrl, expectedBotUsername) {
  if (!rawUrl || !expectedBotUsername) return null;
  let url = rawUrl.trim();
  if (!/^https?:\/\//i.test(url)) url = 'https://' + url;
  let parsed;
  try {
    parsed = new URL(url);
  } catch (e) {
    return null;
  }
  const host = parsed.hostname.toLowerCase();
  if (host !== 't.me' && host !== 'telegram.me') return null;
  const botFromPath = parsed.pathname.replace(/^\//, '');
  if (botFromPath.toLowerCase() !== expectedBotUsername.toLowerCase()) return null;
  const startParam = parsed.searchParams.get('startapp') || parsed.searchParams.get('start');
  if (!startParam) return null;
  const match = startParam.match(/^item_(\d+)$/);
  return match ? match[1] : null;
}

// Перемешивает копию массива (Fisher–Yates), не трогая исходный.
function shuffleAds(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const AdCarousel = ({ onOrderClick, botUsername, onItemLinkClick, serverAds }) => {
  const defaultAd = (
    <div className="ad-banner-card" style={{background: 'linear-gradient(135deg, var(--text-secondary), var(--border-strong))'}}>
      <div className="ad-badge">Реклама</div>
      <div className="ad-icon">📢</div>
      <h4>Ваша реклама</h4>
      <p>Привлеките внимание игроков.</p>
      <div className="ad-bottom">
        <button className="ad-btn" onClick={(e) => { e.stopPropagation(); onOrderClick(); }}>Заказать</button>
      </div>
    </div>
  );

  // Порядок повторов реальных реклам вычисляется ОДИН раз на список serverAds через useMemo,
  // а не при каждом рендере. Раньше shuffle пересчитывался заново на любой ре-рендер карусели
  // (а смена currentIndex при автопрокрутке/скролле/свайпе тоже вызывает рендер) — из-за этого
  // объявление, которое пользователь листал, внезапно подменялось другим прямо во время
  // просмотра. Теперь последовательность стабильна вперёд на весь список, а не на лету.
  const adSequence = useMemo(() => {
    if (serverAds.length === 0) return [];
    const MIN_REAL_SLOTS = 9;
    // Размер одной группы между карточками «Заказать рекламу»: 3, если реальных реклам
    // 3 и больше, иначе — сколько есть (1 или 2). Каждая группа собирается НЕЗАВИСИМО:
    // shuffle(serverAds).slice(0, GROUP_SIZE) — то есть срез одного перемешивания
    // уникальных объявлений, поэтому повтор внутри одной группы невозможен физически.
    // Группы друг от друга никак не связаны — какие именно рекламы попадут в
    // конкретную группу и в каком порядке, каждый раз разное, поэтому по всей
    // карусели реклама естественно повторяется (если её меньше, чем нужно для
    // заполнения ленты) и порядок повторения каждый раз новый.
    const GROUP_SIZE = Math.min(3, serverAds.length);
    const numGroups = Math.max(3, Math.ceil(MIN_REAL_SLOTS / GROUP_SIZE));
    const groups = [];
    for (let g = 0; g < numGroups; g++) {
      groups.push(shuffleAds(serverAds).slice(0, GROUP_SIZE));
    }
    return groups;
  }, [serverAds]);

  let ads = [];
  if (serverAds.length === 0) {
    ads = [defaultAd];
  } else {
    let adIndex = 0;
    adSequence.forEach((group, groupIndex) => {
      group.forEach((ad) => {
        const index = adIndex;
        ads.push(
          <div key={`ad-${index}`} className="ad-banner-card" style={{backgroundImage: `url(${ad.image_url})`, backgroundSize: ad.bg_size || 'cover', backgroundPosition: ad.bg_position}}>
            <div className="ad-overlay"></div>
            <div className="ad-badge">Реклама</div>
            <h4 style={{position: 'relative', zIndex: 2}}>{ad.title}</h4>
            <p style={{position: 'relative', zIndex: 2}}>{ad.description}</p>
            <div className="ad-bottom" style={{position: 'relative', zIndex: 2}}>
              <button className="ad-btn" onClick={(e) => {
                e.stopPropagation();
                if (ad.link_url) {
                  const internalItemId = parseInternalItemLink(ad.link_url, botUsername);
                  if (internalItemId && onItemLinkClick) {
                    onItemLinkClick(internalItemId);
                    return;
                  }
                  let url = ad.link_url;
                  if (!url.startsWith('http://') && !url.startsWith('https://')) url = 'https://' + url;
                  window.Telegram?.WebApp?.openLink(url);
                }
              }}>{ad.button_text}</button>
            </div>
            <div style={{position: 'relative', zIndex: 2, textAlign: 'right', marginTop: '4px'}}>
              <span className="ad-author">от {ad.author_nickname || 'Игрока'}</span>
            </div>
          </div>
        );
        adIndex++;
      });
      // После каждой группы (до 3 разных реклам без повторов внутри группы) — карточка "Заказать рекламу"
      ads.push(React.cloneElement(defaultAd, { key: `def-${groupIndex}` }));
    });
  }

  const isSingle = ads.length === 1;
  const extendedAds = isSingle ? ads : [ads[ads.length - 1], ...ads, ads[0]];
  const [currentIndex, setCurrentIndex] = useState(isSingle ? 0 : 1);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isInteracting, setIsInteracting] = useState(false);

  useEffect(() => {
    if (serverAds.length > 0) {
      setCurrentIndex(1);
      setIsTransitioning(false);
    }
  }, [serverAds]);
  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);
  const containerRef = useRef(null);
  const wheelTimeout = useRef(null);
  const touchStartX = useRef(0);

  const nextAd = useCallback(() => {
    if (isSingle || currentIndex >= extendedAds.length - 1) return;
    setIsTransitioning(true);
    setCurrentIndex(prev => prev + 1);
  }, [currentIndex, extendedAds.length, isSingle]);

  const prevAd = useCallback(() => {
    if (isSingle || currentIndex <= 0) return;
    setIsTransitioning(true);
    setCurrentIndex(prev => prev - 1);
  }, [currentIndex, isSingle]);

  const startAutoScroll = useCallback((delay) => {
    if (isSingle) return;
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setIsTransitioning(true);
      setCurrentIndex(prev => prev + 1);
    }, delay);
  }, [isSingle]);

  useEffect(() => {
    if (!isInteracting) {
      startAutoScroll(5000);
    }
    return () => clearInterval(intervalRef.current);
  }, [isInteracting, startAutoScroll]);

  const handleInteraction = useCallback(() => {
    setIsInteracting(true);
    clearInterval(intervalRef.current);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setIsInteracting(false);
    }, 10000);
  }, []);

  const handleTransitionEnd = () => {
    if (isSingle) return;
    if (currentIndex === 0) {
      setIsTransitioning(false);
      setCurrentIndex(ads.length);
    } else if (currentIndex === extendedAds.length - 1) {
      setIsTransitioning(false);
      setCurrentIndex(1);
    }
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el || isSingle) return;
    const onWheel = (e) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        if (wheelTimeout.current) return;
        handleInteraction();
        if (e.deltaY > 0) nextAd();
        else prevAd();
        wheelTimeout.current = setTimeout(() => { wheelTimeout.current = null; }, 600);
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [nextAd, prevAd, handleInteraction, isSingle]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    handleInteraction();
  };
  const handleTouchEnd = (e) => {
    if (isSingle) return;
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;
    if (diff > 50) nextAd();
    else if (diff < -50) prevAd();
  };

  return (
    <div 
      className="ad-carousel-container scroll-wrapper" 
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleInteraction}
    >
      {!isSingle && <button className="scroll-arrow left" onClick={(e) => { e.stopPropagation(); handleInteraction(); prevAd(); }}>‹</button>}
      <div className="ad-carousel-viewport">
        <div 
          className="ad-carousel-track" 
          style={{ 
            transform: `translateX(-${currentIndex * 100}%)`,
            transition: isTransitioning ? 'transform 0.4s ease-in-out' : 'none'
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          {extendedAds.map((ad, i) => (
            <div key={i} className="ad-carousel-slide">
              {ad}
            </div>
          ))}
        </div>
      </div>
      {!isSingle && <button className="scroll-arrow right" onClick={(e) => { e.stopPropagation(); handleInteraction(); nextAd(); }}>›</button>}
    </div>
  );
};

// Список цветов продвижения должен буквально совпадать со списком на
// сервере (server/routes/market.js, PROMO_COLORS) — там строгая проверка
// `PROMO_COLORS.includes(color)`. Раньше здесь использовались CSS-переменные
// (var(--accent-dark) и т.п.), которые меняются в зависимости от темы и
// никогда не совпадали с хекс-значениями на сервере — из-за этого запрос
// продвижения падал с "Выберите цвет из списка", хотя цвет был выбран.
// Плюс 'var(--danger)' был продублирован дважды — два одинаковых кружка.
// Теперь палитра — точные хекс-значения, идентичные серверным.
// 16 стандартных цветов красителей Minecraft, в порядке: чёрный, белый,
// синий, коричневый, голубой, бирюзовый, зелёный, фиолетовый, жёлтый,
// красный, пурпурный, серый, оранжевый, светло-серый, лаймовый, розовый.
// Список должен буквально совпадать с server/routes/market.js — сервер
// сверяет цвет строго по этому списку (см. /promote).
const PROMO_COLORS = ['#1d1d21', '#f9fffe', '#3c44aa', '#835432', '#3ab3da', '#169c9c', '#5e7c16', '#8932b8', '#fed83d', '#b02e26', '#c74ebd', '#474f52', '#f9801d', '#9d9d97', '#80c71f', '#f38baa'];

// Подбирает чёрный или белый цвет текста так, чтобы он не сливался с
// произвольным цветом фона (например, ярлыком продвижения товара). Считает
// относительную яркость фона по формуле WCAG — на светлых цветах (белый,
// жёлтый, лаймовый, светло-серый, розовый, голубой) возвращает чёрный текст,
// на тёмных — белый.
function getContrastTextColor(hexColor) {
  if (!hexColor) return '#fff';
  const hex = hexColor.replace('#', '');
  if (hex.length !== 6) return '#fff';
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;
  const toLinear = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  return luminance > 0.5 ? '#000000' : '#ffffff';
}

// Размер одной порции товаров витрины. Значение используется и при первой загрузке,
// и при подгрузке при прокрутке, и как нижняя граница для фонового обновления —
// поэтому вынесено в одну константу, чтобы эти места не разъехались.
const PAGE_SIZE = 21;

function Market({ tgId, pendingItemId, onPendingItemHandled, onInitialLoad }) {
  const [search, setSearch] = useState('');
  const [searchFocus, setSearchFocus] = useState(false);
  const [items, setItems] = useState([]);
  const [marketCatalog, setMarketCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  // Гейт для первой полной загрузки вкладки: пока не false, вместо всего
  // содержимого маркета показываем один общий экран "Загрузка" — как просил
  // пользователь, "чтобы вкладка маркет загружалась только когда на ней
  // загрузятся все элементы". После первого успешного захода остаётся true
  // до следующего перезахода на вкладку (компонент пересоздаётся заново).
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [catalogReady, setCatalogReady] = useState(false);
  const [itemsReady, setItemsReady] = useState(false);
  const [serverAds, setServerAds] = useState([]);
  const [adsReady, setAdsReady] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  // Общее количество товаров, подходящих под текущий фильтр (приходит с сервера).
  // Именно оно показывается в заголовке витрины и определяет, есть ли что подгружать,
  // — размер последней подгруженной порции для этого не годится.
  const [totalItems, setTotalItems] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  // Сколько пользовательских загрузок (первая порция / подгрузка при прокрутке)
  // выполняется прямо сейчас. Фоновое обновление в это время не стартует, а подгрузка
  // следующей порции не запускается, пока список может измениться под ней.
  const inFlightRef = useRef(0);
  // Номер последней пользовательской загрузки: ответ применяется, только если за время
  // запроса не началась новая. Так ответ по старой категории не перезапишет новую выдачу.
  const fetchSeqRef = useRef(0);
  const observerTarget = useRef(null);
  // Актуальная длина списка в ref: её читают фоновое обновление и подгрузка,
  // чтобы не пересоздавать свои колбэки на каждое изменение items.
  const itemsCountRef = useRef(0);
  const [showAdInfo, setShowAdInfo] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [adPrice, setAdPrice] = useState(1);
  
  // Order Form State
  const [adTitle, setAdTitle] = useState('');
  const [adDesc, setAdDesc] = useState('');
  const [adBtn, setAdBtn] = useState('');
  const [adLink, setAdLink] = useState('');
  const [adImage, setAdImage] = useState(null);
  const [bgPos, setBgPos] = useState({ x: 50, y: 50 });
  const [bgSize, setBgSize] = useState(100);
  const [pinchDist, setPinchDist] = useState(null);
  const [adDays, setAdDays] = useState(1);
  const [orderError, setOrderError] = useState('');
  const [isOrdering, setIsOrdering] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const previewRef = useRef(null);
  const [toast, setToast] = useState(null);

  // Мои товары / продажа
  const [showMyItems, setShowMyItems] = useState(false);
  const [myItems, setMyItems] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [myAccounts, setMyAccounts] = useState([]);
  const [myAccountsLoaded, setMyAccountsLoaded] = useState(false);
  const [myPayoutAccount, setMyPayoutAccount] = useState(0);
  const [payoutSaving, setPayoutSaving] = useState(false);
  const [showSellModal, setShowSellModal] = useState(false);
  const [sellName, setSellName] = useState('');
  const [selectedCatalogItem, setSelectedCatalogItem] = useState(null);
  const [sellNameFocus, setSellNameFocus] = useState(false);
  const [sellLots, setSellLots] = useState('');
  const [sellUnitQty, setSellUnitQty] = useState('');
  const [sellPrice, setSellPrice] = useState('');
  const [sellDesc, setSellDesc] = useState('');
  const [sellError, setSellError] = useState('');
  const [isSelling, setIsSelling] = useState(false);
  const [topupModal, setTopupModal] = useState(null); // { item }
  const [topupLots, setTopupLots] = useState('');
  const [topupError, setTopupError] = useState('');
  const [isTopupSubmitting, setIsTopupSubmitting] = useState(false);
  const [deleteItemConfirm, setDeleteItemConfirm] = useState(null); // товар, ожидающий подтверждения удаления
  const [isDeletingMyItem, setIsDeletingMyItem] = useState(false);
  const [priceEdits, setPriceEdits] = useState({});
  const [priceSavingId, setPriceSavingId] = useState(null);
  const [promoteModal, setPromoteModal] = useState(null); // { item }
  const [descModal, setDescModal] = useState(null); // { item }
  const [itemDetail, setItemDetail] = useState(null); // развёрнутая карточка товара
  const [botUsername, setBotUsername] = useState('');

  // Корзина
  const [cart, setCart] = useState({}); // { item_id: lots }
  const [cartItems, setCartItems] = useState([]);
  const [cartTotalLots, setCartTotalLots] = useState(0);
  const [cartTotalPrice, setCartTotalPrice] = useState(0);
  // Локальная (оптимистичная) сумма лотов в корзине — считается прямо из cart, который
  // обновляется МГНОВЕННО при клике. cartTotalLots же обновляется только через fetchCart()
  // после серверного подтверждения (с debounce ~350мс) — раньше именно от него зависело,
  // появляется ли плавающая кнопка корзины, из-за чего она выскакивала с заметной задержкой
  // после первого добавления товара.
  const localCartLots = useMemo(() => Object.values(cart).reduce((sum, n) => sum + n, 0), [cart]);
  const [showCart, setShowCart] = useState(false);
  const [cartBusyId, setCartBusyId] = useState(null);
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  // Отслеживание статуса активных заказов для всплывающего окошка
  const [activeOrders, setActiveOrders] = useState([]);
  const [orderPopupDismissed, setOrderPopupDismissed] = useState(false);
  const [orderPopupExpanded, setOrderPopupExpanded] = useState(false);
  const [descText, setDescText] = useState('');
  const [descError, setDescError] = useState('');
  const [isSavingDesc, setIsSavingDesc] = useState(false);
  const [promoteDays, setPromoteDays] = useState('');
  const [promoteColor, setPromoteColor] = useState('#1d1d21');
  const [promoteLabel, setPromoteLabel] = useState('');
  const [promotePricing, setPromotePricing] = useState({ price_per_day: 5, label_price: 15 });
  const [promoteError, setPromoteError] = useState('');
  const [isPromoting, setIsPromoting] = useState(false);

  const showToastMsg = (msg, isError = false) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 3000);
  };

  const copyToClipboard = (text) => {
    copyToClipboardUtil(text, () => showToastMsg('Ссылка скопирована'), () => showToastMsg('Ошибка копирования', true));
  };

  // Username бота нужен для сборки прямой ссылки на приложение — грузим один раз
  useEffect(() => {
    fetch('/api/market/bot-username')
      .then(res => res.json())
      .then(data => { if (data.username) setBotUsername(data.username); })
      .catch(e => console.error('Не удалось получить username бота', e));
  }, []);

  const handleShareItem = (item) => {
    if (!item) return;
    if (!botUsername) {
      showToastMsg('Не удалось получить ссылку, попробуйте ещё раз чуть позже', true);
      return;
    }
    const link = `https://t.me/${botUsername}?startapp=item_${item.id}`;
    copyToClipboard(link);
  };

  // Открывает карточку товара по его id прямо внутри приложения, без
  // перехода по ссылке и перезагрузки мини-приложения. Используется и при
  // запуске приложения по ссылке "поделиться" (см. эффект ниже), и при
  // клике по кнопке рекламного баннера, если её ссылка ведёт на товар в
  // нашем же боте (см. AdCarousel/parseInternalItemLink).
  const openItemById = useCallback((itemId) => {
    fetch(`/api/market/item/${itemId}`)
      .then(res => res.json().then(data => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (ok && data.item) {
          setItemDetail(data.item);
        } else {
          showToastMsg(data.error || 'Товар не найден или уже продан', true);
        }
      })
      .catch(e => console.error('Не удалось открыть товар по ссылке', e));
  }, []);

  // Если приложение открыли по ссылке на конкретный товар (кнопка "поделиться"
  // формирует такую ссылку) — сразу открываем его карточку
  useEffect(() => {
    if (!pendingItemId) return;
    openItemById(pendingItemId);
    if (onPendingItemHandled) onPendingItemHandled();
  }, [pendingItemId, openItemById, onPendingItemHandled]);

  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      setBgSize(prev => Math.max(10, Math.min(500, prev + e.deltaY * -0.1)));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [adImage]);

  // Загрузка порции товаров по СМЕЩЕНИЮ (а не по номеру страницы): смещение всегда
  // равно фактическому числу уже показанных карточек, поэтому подгрузка не зависит
  // от того, сколько раз список успел обновиться в фоне.
  //   offset === 0  — список заменяется целиком: при обычном вызове это смена поиска или
  //                   категории, при фоновом — обновление всех уже показанных товаров
  //                   (limitOverride задаёт, сколько их перезабрать);
  //   offset  >  0  — дописываем следующую порцию в конец списка.
  const fetchItems = useCallback(async (offset, searchQuery, categoryQuery, isBackground = false, limitOverride = null) => {
    // Фоновое обновление собрано по текущей длине списка, поэтому во время
    // пользовательской загрузки оно бессмысленно: его ответ пришёл бы последним
    // и затёр бы только что подгруженные товары. Пропускаем такой такт.
    if (isBackground && inFlightRef.current > 0) return;

    const seq = isBackground ? fetchSeqRef.current : ++fetchSeqRef.current;
    if (!isBackground) inFlightRef.current += 1;
    try {
      const limit = limitOverride || PAGE_SIZE;
      const params = new URLSearchParams();
      if (searchQuery) params.set('q', searchQuery);
      if (categoryQuery) params.set('category', categoryQuery);
      params.set('offset', offset);
      params.set('limit', limit);
      const res = await fetch(`/api/market?${params.toString()}`);
      const data = await res.json();
      // Пока ответ шёл по сети, пользователь мог сменить категорию или строку поиска —
      // тогда эти товары уже не от текущей выдачи, применять их нельзя.
      if (seq !== fetchSeqRef.current) return;

      const fetchedItems = data.items || [];
      if (typeof data.total === 'number') setTotalItems(data.total);

      setItems(prev => {
        if (offset === 0) {
          // Первая порция: и обычная загрузка, и фоновое обновление заменяют список
          // целиком. Для фонового обновления запрошено ровно столько товаров, сколько
          // уже показано, поэтому замена ничего не обрезает — а если товаров реально
          // стало меньше (раскупили), список честно укоротится.
          if (isBackground && JSON.stringify(prev) === JSON.stringify(fetchedItems)) return prev;
          return fetchedItems;
        }

        // Подгрузка следующей порции — дописываем только те id, которых ещё нет
        const existingIds = new Set(prev.map(i => i.id));
        const newUnique = fetchedItems.filter(i => !existingIds.has(i.id));
        if (newUnique.length === 0) return prev;
        return [...prev, ...newUnique];
      });
    } catch (err) {
      console.error('Ошибка загрузки маркета:', err);
    } finally {
      if (!isBackground) inFlightRef.current -= 1;
    }
  }, []);

  useEffect(() => { itemsCountRef.current = items.length; }, [items.length]);

  // «Есть ещё товары» определяем по общему количеству с сервера, а не по размеру
  // последней порции: надпись «Загрузка...» исчезает ровно тогда, когда показано всё.
  useEffect(() => {
    setHasMore(totalItems !== null && items.length < totalItems);
  }, [items.length, totalItems]);

  // Каталог для подсказок в строке поиска — здесь нужен ВЕСЬ ассортимент, поэтому
  // явно просим большой лимит. Без него запрос уходил с лимитом по умолчанию (30),
  // и подсказки искались лишь по первым тридцати товарам витрины.
  useEffect(() => {
    fetch('/api/market?limit=1000')
      .then(res => res.json())
      .then(data => { setMarketCatalog(data.items || []); setCatalogReady(true); })
      .catch(err => { console.error('Ошибка загрузки каталога для поиска:', err); setCatalogReady(true); });
  }, []);

  // Рекламный баннер тоже грузим здесь, до появления вкладки — раньше
  // AdCarousel сам делал этот запрос уже ПОСЛЕ того, как вкладка появлялась,
  // из-за чего пользователь сначала на долю секунды видел карточку-заглушку
  // "Ваша реклама" (или дефолтную рекламу), а затем её резко подменяло
  // настоящее объявление другого игрока. Теперь первый показ уже содержит
  // готовые данные, без рывка.
  // Вынесено в отдельную функцию (а не только в useEffect), чтобы её можно было
  // вызвать сразу после успешного заказа рекламы — тогда своя новая реклама
  // появляется в карусели маркета мгновенно, не дожидаясь ближайшего опроса.
  const fetchAds = useCallback(async () => {
    try {
      const res = await fetch('/api/market/ads');
      const data = await res.json();
      const next = data.ads || [];
      // Считаем список рекламы «изменившимся» только если поменялся сам НАБОР
      // объявлений (по id и содержимому), а не их порядок. Сервер больше не
      // тасует выдачу, но эта сигнатура — доп. защита: при обычном опросе раз в
      // 2 сек ссылка serverAds остаётся прежней, поэтому карусель не пересобирает
      // перемешивание и не подменяет карточку, которую игрок сейчас смотрит.
      const adSig = (arr) => JSON.stringify([...arr].sort((a, b) => (a.id || 0) - (b.id || 0)));
      setServerAds(prev => (adSig(prev) !== adSig(next) ? next : prev));
    } catch (e) {
      console.error(e);
    } finally {
      setAdsReady(true);
    }
  }, []);

  useEffect(() => {
    fetchAds();
  }, [fetchAds]);

  useEffect(() => {
    if (items.length === 0) setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchItems(0, search, activeCategory).finally(() => { setLoading(false); setItemsReady(true); });
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [search, activeCategory]);

  // Полная загрузка вкладки считается завершённой, когда готовы ВСЕ три
  // источника данных, из которых собирается основной экран маркета:
  // каталог (нужен для подсказок поиска), первая страница товаров и
  // рекламный баннер (чтобы не было рывка при появлении настоящей рекламы).
  useEffect(() => {
    if (catalogReady && itemsReady && adsReady) {
      setInitialLoadDone(true);
      onInitialLoad && onInitialLoad();
    }
  }, [catalogReady, itemsReady, adsReady]);

  useEffect(() => {
    const interval = setInterval(() => {
      // Фоновое обновление перезабирает ВСЕ уже показанные товары одной порцией.
      // Раньше здесь запрашивалась только первая страница (21 шт.) и ею подменялся
      // весь список — из-за этого каждые 2 секунды подгруженные товары исчезали,
      // витрина навсегда застревала на 21 позиции, а внизу висела «Загрузка...».
      fetchItems(0, search, activeCategory, true, Math.max(PAGE_SIZE, itemsCountRef.current));
      fetchAdPrice();
      fetchAds();
    }, 2000);
    return () => clearInterval(interval);
  }, [search, activeCategory, fetchItems, fetchAds]);

  // Триггер подгрузки теперь не "у самого низа списка", а строго на 5-м ряду
  // (6-й элемент с конца загруженной пачки — при 3 карточках в ряд это 2 ряда
  // "про запас"), см. ref на карточке ниже. Позиция триггера физически смещается
  // при каждой новой подгруженной пачке, поэтому пересоздаём наблюдатель при
  // каждом изменении items.length, а не один раз.
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && inFlightRef.current === 0) {
          // Смещение берём из ref — это всегда реальная длина списка на момент запроса
          fetchItems(itemsCountRef.current, search, activeCategory);
        }
      },
      { threshold: 0.1 }
    );
    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }
    return () => observer.disconnect();
  }, [hasMore, items.length, search, activeCategory, fetchItems]);

  const categories = [
    { name: "Расходники", icon: <img src="/items-icons/Golden_Carrot.png" alt="Расходники" style={{width: '24px', height: '24px', objectFit: 'contain', imageRendering: 'pixelated'}} /> },
    { name: "Ресурсы", icon: <img src="/items-icons/Diamond.png" alt="Ресурсы" style={{width: '24px', height: '24px', objectFit: 'contain', imageRendering: 'pixelated'}} /> },
    { name: "Блоки", icon: <img src="/items-icons/Bricks.png" alt="Блоки" style={{width: '24px', height: '24px', objectFit: 'contain', imageRendering: 'pixelated'}} /> },
    { name: "Инструменты", icon: <img src="/items-icons/Diamond_Pickaxe.png" alt="Инструменты" style={{width: '24px', height: '24px', objectFit: 'contain', imageRendering: 'pixelated'}} /> },
    { name: "Броня", icon: <img src="/items-icons/Diamond_Chestplate.png" alt="Броня" style={{width: '24px', height: '24px', objectFit: 'contain', imageRendering: 'pixelated'}} /> },
    { name: "Книги", icon: <img src="/items-icons/Enchanted_Book.png" alt="Книги" style={{width: '24px', height: '24px', objectFit: 'contain', imageRendering: 'pixelated'}} /> },
    { name: "Предметы MP", icon: <img src="/items-icons/charmer_vault_key.png" alt="Предметы MP" style={{width: '24px', height: '24px', objectFit: 'contain', imageRendering: 'pixelated'}} /> },
    { name: "Шаблоны", icon: <img src="/items-icons/piglin_banner_pattern.png" alt="Шаблоны" style={{width: '24px', height: '24px', objectFit: 'contain', imageRendering: 'pixelated'}} /> },
    { name: "Зелья", icon: <img src="/items-icons/Experience_Bottle.png" alt="Зелья" style={{width: '24px', height: '24px', objectFit: 'contain', imageRendering: 'pixelated'}} /> }
  ];

  const toggleCategory = (catName) => {
    const newCat = activeCategory === catName ? null : catName;
    setActiveCategory(newCat);
    if (newCat) setItems(prev => prev.filter(i => i.category === newCat));
  };

  const fetchAdPrice = async () => {
    try {
      const res = await fetch('/api/market/ad-price');
      const data = await res.json();
      setAdPrice(data.price || 1);
    } catch (e) { console.error(e); }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 3 * 1024 * 1024) return setOrderError('Файл слишком большой (макс 3МБ)');
      const reader = new FileReader();
      reader.onloadend = () => {
        setAdImage(reader.result);
        setBgPos({ x: 50, y: 50 });
      };
      reader.readAsDataURL(file);
    }
  };

  const fetchMyItems = useCallback(async () => {
    try {
      const res = await fetch(`/api/market/my-items/${tgId}`);
      const data = await res.json();
      setMyItems(data.items || []);
      setMyRequests(data.requests || []);
      setMyPayoutAccount(data.payout_account_number || 0);
    } catch (e) { console.error(e); }
  }, [tgId]);

  const fetchMyAccounts = useCallback(async () => {
    try {
      const res = await fetch(`/api/bank/${tgId}/accounts`);
      const data = await res.json();
      setMyAccounts((data.accounts || []).filter(a => a.status === 'ACTIVE'));
    } catch (e) { console.error(e); }
    finally { setMyAccountsLoaded(true); }
  }, [tgId]);

  useEffect(() => {
    if (showMyItems) {
      fetchMyItems();
      fetchMyAccounts();
      const interval = setInterval(() => { fetchMyItems(); fetchMyAccounts(); }, 2000);
      return () => clearInterval(interval);
    }
  }, [showMyItems, fetchMyItems, fetchMyAccounts]);

  const fetchCart = useCallback(async () => {
    try {
      const res = await fetch(`/api/market/cart/${tgId}`);
      const data = await res.json();
      const items = data.items || [];
      setCartItems(items);
      setCartTotalLots(data.total_lots || 0);
      setCartTotalPrice(data.total_price || 0);
      const map = {};
      items.forEach(i => { map[i.item_id] = i.lots; });
      setCart(map);
    } catch (e) { console.error(e); }
  }, [tgId]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  useEffect(() => {
    if (showCart) {
      const interval = setInterval(fetchCart, 2000);
      return () => clearInterval(interval);
    }
  }, [showCart, fetchCart]);

  // Быстрые повторные клики по +/- раньше могли отправлять несколько параллельных
  // запросов на сервер, и ответ на более ранний (но более медленный) запрос иногда
  // прилетал последним и откатывал счётчик назад. Теперь клики только мгновенно
  // обновляют локальное состояние и "ожидаемое" значение, а на сервер уходит
  // только одно (последнее) значение — через небольшую задержку после того,
  // как клики прекратились.
  const cartPendingRef = useRef({});
  const cartTimersRef = useRef({});
  const cartSeqRef = useRef({});

  const commitCartChange = (itemId) => {
    const seq = (cartSeqRef.current[itemId] || 0) + 1;
    cartSeqRef.current[itemId] = seq;
    const targetLots = cartPendingRef.current[itemId];

    setCartBusyId(itemId);
    fetch('/api/market/cart/set', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tg_id: tgId, item_id: itemId, lots: targetLots })
    })
      .then(res => res.json().then(data => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (cartSeqRef.current[itemId] !== seq) return; // это ответ на уже устаревший запрос
        if (!ok) {
          showToastMsg(data.error || 'Ошибка обновления корзины', true);
          fetchCart();
        } else {
          if (data.capped) showToastMsg('Достигнут максимум доступного количества', true);
          fetchCart();
        }
      })
      .catch(() => {
        if (cartSeqRef.current[itemId] !== seq) return;
        showToastMsg('Ошибка соединения', true);
        fetchCart();
      })
      .finally(() => {
        if (cartSeqRef.current[itemId] === seq) setCartBusyId(null);
      });
  };

  const handleCartSet = (item, newLots) => {
    const itemId = item.id || item.item_id;
    const clamped = Math.max(0, newLots);

    cartPendingRef.current[itemId] = clamped;

    // Оптимистичное обновление — отвечает мгновенно на каждый клик
    setCart(prev => {
      const next = {...prev};
      if (clamped === 0) delete next[itemId];
      else next[itemId] = clamped;
      return next;
    });

    if (cartTimersRef.current[itemId]) clearTimeout(cartTimersRef.current[itemId]);
    cartTimersRef.current[itemId] = setTimeout(() => commitCartChange(itemId), 350);
  };

  // Для кнопок +/- — считает следующее значение от последнего локально известного
  // (а не от значения из пропсов/рендера, которое могло ещё не успеть обновиться)
  const stepCart = (item, delta, maxLots) => {
    const itemId = item.id || item.item_id;
    const base = cartPendingRef.current[itemId] !== undefined ? cartPendingRef.current[itemId] : (cart[itemId] || 0);
    let target = base + delta;
    if (maxLots !== undefined && maxLots !== null) target = Math.min(target, maxLots);
    target = Math.max(0, target);
    handleCartSet(item, target);
  };

  const handleCheckout = async () => {
    setIsCheckingOut(true);
    try {
      const bankRes = await fetch(`/api/bank/${tgId}`);
      const bankData = await bankRes.json();
      if (!bankData.has_account) {
        showToastMsg('Откройте счет в банке, чтобы оформить заказ', true);
        return;
      }
      if (!bankData.postal_address || !bankData.postal_address.trim()) {
        showToastMsg('Укажите почтовый адрес в профиле, чтобы оформить заказ', true);
        return;
      }

      const res = await fetch('/api/market/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tg_id: tgId })
      });
      const data = await res.json();
      if (!res.ok) {
        showToastMsg(data.error || 'Ошибка оформления заказа', true);
      } else {
        showToastMsg('Заказ оформлен! Ожидайте сборку.');
        setShowCart(false);
        setCart({});
        setCartItems([]);
        setCartTotalLots(0);
        setCartTotalPrice(0);
        setOrderPopupDismissed(false);
        fetchActiveOrders();
      }
    } catch (e) {
      showToastMsg('Ошибка соединения', true);
    } finally {
      setIsCheckingOut(false);
    }
  };

  const fetchActiveOrders = useCallback(async () => {
    try {
      const res = await fetch(`/api/market/my-active-orders/${tgId}`);
      const data = await res.json();
      setActiveOrders(data.orders || []);
    } catch (e) { /* тихо игнорируем — окошко статуса не критично */ }
  }, [tgId]);

  useEffect(() => {
    fetchActiveOrders();
  }, [fetchActiveOrders]);

  useEffect(() => {
    const hasUnresolved = activeOrders.some(o => o.status === 'PENDING' || o.status === 'ACCEPTED');
    if (!hasUnresolved) return;
    const interval = setInterval(fetchActiveOrders, 2000);
    return () => clearInterval(interval);
  }, [activeOrders, fetchActiveOrders]);

  useEffect(() => {
    const hasUnresolved = activeOrders.some(o => o.status === 'PENDING' || o.status === 'ACCEPTED');
    if (activeOrders.length > 0 && !hasUnresolved) {
      const timeout = setTimeout(() => { setActiveOrders([]); setOrderPopupExpanded(false); }, 10000);
      return () => clearTimeout(timeout);
    }
  }, [activeOrders]);

  const ORDER_STATUS_CFG = {
    PENDING: { icon: '⏳', text: 'Заказ в ожидании', color: 'var(--warning)' },
    ACCEPTED: { icon: '📦', text: 'Заказ приняли в сборку', color: 'var(--accent-dark)' },
    DELIVERED: { icon: '✅', text: 'Доставлен на почту', color: 'var(--success)' },
    CANCELLED: { icon: '❌', text: 'Заказ отменён', color: 'var(--danger)' }
  };
  const STATUS_PRIORITY = { PENDING: 0, ACCEPTED: 1, DELIVERED: 2, CANCELLED: 2 };

  const renderOrderPopup = () => {
    if (activeOrders.length === 0 || orderPopupDismissed) return null;
    const primary = [...activeOrders].sort((a, b) => STATUS_PRIORITY[a.status] - STATUS_PRIORITY[b.status])[0];
    const cfg = ORDER_STATUS_CFG[primary.status];
    if (!cfg) return null;

    if (!orderPopupExpanded) {
      const label = activeOrders.length > 1
        ? `${cfg.text} (ещё ${activeOrders.length - 1})`
        : (primary.status === 'DELIVERED' ? `Доставлен на почту «${primary.postal_address}»` : cfg.text);

      return (
        <div className="order-status-popup" style={{borderLeftColor: cfg.color}} onClick={() => setOrderPopupExpanded(true)}>
          <span className="order-status-icon">{cfg.icon}</span>
          <span className="order-status-text">{label}</span>
          {activeOrders.length > 1 && <span className="order-status-count">{activeOrders.length}</span>}
          <button className="order-status-close" onClick={e => { e.stopPropagation(); setOrderPopupDismissed(true); }}>✕</button>
        </div>
      );
    }

    return (
      <div className="order-status-popup expanded" onClick={() => setOrderPopupExpanded(false)}>
        <div className="order-status-expanded-header">
          <span style={{fontWeight: 700, fontSize: '13px'}}>{activeOrders.length > 1 ? `Заказы (${activeOrders.length})` : 'Заказ'}</span>
          <button className="order-status-close" onClick={e => { e.stopPropagation(); setOrderPopupDismissed(true); }}>✕</button>
        </div>
        {activeOrders.map(o => {
          const c = ORDER_STATUS_CFG[o.status];
          return (
            <div key={o.order_id} className="order-status-line" style={{borderLeftColor: c.color}}>
              <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                <span>{c.icon}</span>
                <span style={{fontSize: '12px', fontWeight: 600}}>{o.status === 'DELIVERED' ? `Доставлен на почту «${o.postal_address}»` : c.text}</span>
              </div>
              <div className="order-status-composition">
                {o.items.map(i => `${i.name} ×${i.qty} шт.`).join(', ')}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const catalogSuggestions = (() => {
    const q = sellName.trim().toLowerCase();
    if (!q || selectedCatalogItem) return [];
    const starts = [];
    const includes = [];
    for (const it of itemsCatalog) {
      const lower = it.name.toLowerCase();
      if (lower.startsWith(q)) starts.push(it);
      else if (lower.includes(q)) includes.push(it);
      if (starts.length >= 30) break;
    }
    return [...starts, ...includes].slice(0, 30);
  })();

  const searchSuggestions = (() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    const starts = [];
    const includes = [];
    const seen = new Set();
    for (const it of marketCatalog) {
      if (seen.has(it.name)) continue;
      const lower = it.name.toLowerCase();
      if (lower.startsWith(q)) { starts.push(it); seen.add(it.name); }
      else if (lower.includes(q)) { includes.push(it); seen.add(it.name); }
      if (starts.length >= 30) break;
    }
    return [...starts, ...includes].slice(0, 15).map(it => ({ id: it.id, name: it.name, image: it.image_url }));
  })();

  const resetSellForm = () => {
    setSellName(''); setSelectedCatalogItem(null); setSellLots(''); setSellUnitQty('');
    setSellPrice(''); setSellDesc(''); setSellError('');
  };

  const handleSellSubmit = async () => {
    setSellError('');
    if (!selectedCatalogItem || selectedCatalogItem.name !== sellName.trim()) return setSellError('Выберите товар из списка (начните вводить название)');
    const lots = parseInt(sellLots, 10);
    const unitQty = parseInt(sellUnitQty, 10);
    const price = parseInt(sellPrice, 10);
    if (!unitQty || unitQty <= 0) return setSellError('Укажите количество товара в одном лоте продажи');
    if (!lots || lots <= 0) return setSellError('Укажите количество лотов, которое вы отправите');
    if (!price || price <= 0) return setSellError('Укажите корректную цену за лот');

    setIsSelling(true);
    try {
      const res = await fetch('/api/market/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tg_id: tgId,
          type: 'LISTING',
          item_name: sellName,
          lots,
          unit_qty: unitQty,
          price,
          description: sellDesc
        })
      });
      const data = await res.json();
      if (!res.ok) {
        setSellError(data.error || 'Ошибка отправки заявки');
      } else {
        setShowSellModal(false);
        resetSellForm();
        showToastMsg('Заявка отправлена! Отправьте товар на почту Azon и ожидайте одобрения.');
        if (showMyItems) fetchMyItems();
      }
    } catch (e) {
      setSellError('Ошибка соединения');
    } finally {
      setIsSelling(false);
    }
  };

  const handleTopupSubmit = async () => {
    setTopupError('');
    const lots = parseInt(topupLots, 10);
    if (!lots || lots <= 0) return setTopupError('Укажите количество лотов, которое вы отправите');

    setIsTopupSubmitting(true);
    try {
      const res = await fetch('/api/market/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tg_id: tgId,
          type: 'TOPUP',
          item_id: topupModal.item.id,
          lots
        })
      });
      const data = await res.json();
      if (!res.ok) {
        setTopupError(data.error || 'Ошибка отправки заявки');
      } else {
        setTopupModal(null);
        setTopupLots('');
        showToastMsg('Заявка на пополнение отправлена! Отправьте товар на почту Azon.');
        fetchMyItems();
      }
    } catch (e) {
      setTopupError('Ошибка соединения');
    } finally {
      setIsTopupSubmitting(false);
    }
  };

  const handleSavePrice = async (item) => {
    const newPrice = parseInt(priceEdits[item.id], 10);
    if (!newPrice || newPrice <= 0) return showToastMsg('Укажите корректную цену', true);
    if (newPrice === item.price) return;

    setPriceSavingId(item.id);
    try {
      const res = await fetch('/api/market/update-price', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tg_id: tgId, item_id: item.id, price: newPrice })
      });
      const data = await res.json();
      if (!res.ok) {
        showToastMsg(data.error || 'Ошибка изменения цены', true);
      } else {
        showToastMsg('Цена обновлена');
        fetchMyItems();
      }
    } catch (e) {
      showToastMsg('Ошибка соединения', true);
    } finally {
      setPriceSavingId(null);
    }
  };

  const handleSavePayoutAccount = async (accountNumber) => {
    const prev = myPayoutAccount;
    setMyPayoutAccount(accountNumber);
    setPayoutSaving(true);
    try {
      const res = await fetch('/api/market/update-payout-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tg_id: tgId, account_number: accountNumber })
      });
      const data = await res.json();
      if (!res.ok) {
        showToastMsg(data.error || 'Ошибка изменения счёта', true);
        setMyPayoutAccount(prev);
      } else {
        showToastMsg('Счёт зачисления обновлён');
      }
    } catch (e) {
      showToastMsg('Ошибка соединения', true);
      setMyPayoutAccount(prev);
    } finally {
      setPayoutSaving(false);
    }
  };

  const handleDeleteItem = async (item) => {
    setIsDeletingMyItem(true);
    try {
      const res = await fetch('/api/market/delete-item', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tg_id: tgId, item_id: item.id })
      });
      const data = await res.json();
      if (!res.ok) {
        showToastMsg(data.error || 'Ошибка удаления товара', true);
      } else {
        showToastMsg('Товар удалён');
        setDeleteItemConfirm(null);
        fetchMyItems();
      }
    } catch (e) {
      showToastMsg('Ошибка соединения', true);
    } finally {
      setIsDeletingMyItem(false);
    }
  };

  const fetchPromotePricing = async () => {
    try {
      const res = await fetch('/api/market/promotion-price');
      const data = await res.json();
      setPromotePricing(data);
    } catch (e) { console.error(e); }
  };

  const openPromoteModal = (item) => {
    setPromoteModal({ item });
    setPromoteDays('');
    setPromoteColor('var(--accent-dark)');
    setPromoteLabel('');
    setPromoteError('');
    fetchPromotePricing();
  };

  const promoteTotalPrice = (() => {
    const days = parseInt(promoteDays, 10) || 0;
    const labelCost = promoteLabel.trim() ? days * promotePricing.label_price : 0;
    return days * promotePricing.price_per_day + labelCost;
  })();

  const openDescModal = (item) => {
    setDescModal({ item });
    setDescText(item.description || '');
    setDescError('');
  };

  const handleSaveDescription = async () => {
    setDescError('');
    setIsSavingDesc(true);
    try {
      const res = await fetch('/api/market/update-description', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tg_id: tgId, item_id: descModal.item.id, description: descText })
      });
      const data = await res.json();
      if (!res.ok) {
        setDescError(data.error || 'Ошибка сохранения описания');
      } else {
        setDescModal(null);
        showToastMsg('Описание обновлено');
        fetchMyItems();
      }
    } catch (e) {
      setDescError('Ошибка соединения');
    } finally {
      setIsSavingDesc(false);
    }
  };

  const handlePromoteSubmit = async () => {
    setPromoteError('');
    const days = parseInt(promoteDays, 10);
    if (!days || days <= 0) return setPromoteError('Укажите количество дней');
    if (!promoteColor) return setPromoteError('Выберите цвет');

    setIsPromoting(true);
    try {
      const res = await fetch('/api/market/promote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tg_id: tgId,
          item_id: promoteModal.item.id,
          days,
          color: promoteColor,
          label: promoteLabel.trim()
        })
      });
      const data = await res.json();
      if (!res.ok) {
        setPromoteError(data.error || 'Ошибка покупки продвижения');
      } else {
        setPromoteModal(null);
        showToastMsg(`Товар продвинут! Списано ${data.total_price} сц`);
        fetchMyItems();
      }
    } catch (e) {
      setPromoteError('Ошибка соединения');
    } finally {
      setIsPromoting(false);
    }
  };

  const handleDragStart = (e) => {
    setIsDragging(true);
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setDragStart({ x: clientX, y: clientY });
  };

  const handleDragMove = (e) => {
    if (e.touches && e.touches.length === 2) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      if (pinchDist) {
        setBgSize(prev => Math.max(10, Math.min(500, prev + (dist - pinchDist) * 0.5)));
      }
      setPinchDist(dist);
    } else if (isDragging) {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const dx = dragStart.x - clientX;
      const dy = dragStart.y - clientY;
      
      setBgPos(prev => ({
        x: Math.max(0, Math.min(100, prev.x + dx * 0.2)),
        y: Math.max(0, Math.min(100, prev.y + dy * 0.2))
      }));
      setDragStart({ x: clientX, y: clientY });
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setPinchDist(null);
  };

  const calculateDays = () => {
    const days = parseInt(adDays, 10);
    return days > 0 ? days : 0;
  };

  const handleOrderSubmit = async () => {
    setOrderError('');
    if (!adTitle || adTitle.length > 30) return setOrderError('Заголовок обязателен (до 30 симв.)');
    if (!adDesc || adDesc.length > 80) return setOrderError('Описание обязательно (до 80 симв.)');
    if (!adBtn || adBtn.length > 15) return setOrderError('Текст кнопки обязателен (до 15 симв.)');
    if (!adImage) return setOrderError('Загрузите фоновое изображение');

    const days = calculateDays();
    if (!days || days < 1) return setOrderError('Укажите количество суток рекламы (минимум 1)');

    const totalPrice = days * adPrice;

    setIsOrdering(true);
    try {
      const res = await fetch('/api/market/ads/order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tg_id: tgId,
          title: adTitle,
          description: adDesc,
          button_text: adBtn,
          link_url: adLink,
          image_url: adImage,
          bg_position: `${bgPos.x}% ${bgPos.y}%`,
          bg_size: `${bgSize}%`,
          days
        })
      });
      const data = await res.json();
      if (!res.ok) {
        setOrderError(data.error || 'Ошибка заказа');
      } else {
        setShowOrderModal(false);
        setShowAdInfo(false);
        fetchAds();
        showToastMsg('Реклама оплачена и уже опубликована на маркетплейсе!');
      }
    } catch (e) {
      setOrderError('Ошибка соединения');
    } finally {
      setIsOrdering(false);
    }
  };

  if (showMyItems) {
    const statusLabel = (r) => {
      if (r.status === 'PENDING') return { text: '⏳ На рассмотрении', color: 'var(--warning)' };
      if (r.status === 'APPROVED') return { text: '✅ Одобрено', color: 'var(--success)' };
      return { text: '❌ Отклонено', color: 'var(--danger)' };
    };

    return (
      <div className="market-page">
        {toast && <div className={`toast ${toast.isError ? 'error' : ''}`}>{toast.msg}</div>}
        {renderOrderPopup()}
        <button className="secondary-btn" onClick={() => setShowMyItems(false)} style={{width: 'auto', padding: '8px 15px', marginBottom: '15px'}}>← Назад</button>

        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
          <h3 style={{margin: 0}}>Мои товары</h3>
          <button className="primary-btn" style={{width: 'auto', padding: '10px 16px'}} onClick={async () => {
            try {
              const res = await fetch(`/api/bank/${tgId}`);
              const data = await res.json();
              if (!data.has_account) {
                showToastMsg('Откройте счет в банке, чтобы выставлять товары', true);
                return;
              }
              if (!data.postal_address || !data.postal_address.trim()) {
                showToastMsg('Укажите почтовый адрес в профиле, чтобы выставлять товары', true);
                return;
              }
              setShowSellModal(true);
            } catch (e) {
              showToastMsg('Ошибка проверки счета', true);
            }
          }}>+ Продать товар</button>
        </div>

        {myAccounts.length > 0 ? (
          <div className="card" style={{marginBottom: '15px'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px'}}>
              <span style={{fontSize: '14px', fontWeight: 'bold'}}>Счёт зачисления выручки</span>
              <select
                value={myPayoutAccount || myAccounts[0].account_number}
                disabled={payoutSaving}
                onChange={e => handleSavePayoutAccount(parseInt(e.target.value, 10))}
                style={{flex: '0 0 auto', width: 'auto', minWidth: '160px'}}
              >
                {myAccounts.map(a => (
                  <option key={a.id} value={a.account_number}>
                    #{a.account_number} ({a.account_name})
                  </option>
                ))}
              </select>
            </div>
            <p style={{fontSize: '12px', color: 'var(--text-secondary)', margin: '8px 0 0 0'}}>
              Вся выручка со всех ваших товаров будет приходить на этот счёт.
            </p>
          </div>
        ) : myAccountsLoaded ? (
          // Ни одного активного счёта нет (не открыт, либо все заблокированы/удалены) —
          // без счёта для зачисления выручки все товары продавца скрыты из маркета
          // (см. фильтр в server/routes/market.js), поэтому явно предупреждаем здесь,
          // а не оставляем игрока гадать, почему товары никто не видит и не покупает.
          // Показываем это предупреждение ТОЛЬКО после того, как список счетов реально
          // загрузился (myAccountsLoaded) — иначе в момент открытия страницы, пока запрос
          // ещё летит, здесь на долю секунды мелькало бы это предупреждение даже у тех,
          // у кого счета есть.
          <div className="card" style={{marginBottom: '15px', border: '1.5px solid var(--danger)'}}>
            <p style={{margin: 0, fontSize: '13px', color: 'var(--danger)', fontWeight: 'bold'}}>
              ⚠️ У вас нет ни одного активного счёта для зачисления выручки. Пока не откроете счёт в банке (или не разблокируете хотя бы один существующий), все ваши товары скрыты из маркетплейса и недоступны для покупки другими игроками.
            </p>
          </div>
        ) : null}

        {myRequests.filter(r => r.status === 'PENDING').length > 0 && (
          <div className="card">
            <h4 style={{marginTop: 0}}>Заявки на рассмотрении</h4>
            {myRequests.filter(r => r.status === 'PENDING').map(r => (
              <div key={r.request_id} style={{borderBottom: '1px solid var(--bg-subtle)', padding: '10px 0'}}>
                <p style={{margin: '2px 0', fontSize: '14px'}}><b>{r.item_name}</b> · {r.type === 'LISTING' ? 'выставление' : 'пополнение'}</p>
                <p style={{margin: '2px 0', fontSize: '13px', color: 'var(--text-secondary)'}}>Лотов: {r.unit_qty ? Math.round(r.total_qty / r.unit_qty) : '—'} × {r.unit_qty} шт. = {r.total_qty} шт. · Цена за лот: {r.price} сц</p>
                <span style={{fontSize: '12px', fontWeight: 'bold', color: statusLabel(r).color}}>{statusLabel(r).text}</span>
              </div>
            ))}
          </div>
        )}

        {myRequests.filter(r => r.status === 'REJECTED').length > 0 && (
          <div className="card">
            <h4 style={{marginTop: 0}}>Отклонённые заявки</h4>
            {myRequests.filter(r => r.status === 'REJECTED').slice(0, 5).map(r => (
              <div key={r.request_id} style={{borderBottom: '1px solid var(--bg-subtle)', padding: '10px 0'}}>
                <p style={{margin: '2px 0', fontSize: '14px'}}><b>{r.item_name}</b> · {r.type === 'LISTING' ? 'выставление' : 'пополнение'}</p>
                <span style={{fontSize: '12px', fontWeight: 'bold', color: 'var(--danger)'}}>❌ Отклонено</span>
                {r.reject_reason && <p style={{margin: '4px 0 0 0', fontSize: '12px', color: 'var(--danger)'}}>Причина: {r.reject_reason}</p>}
              </div>
            ))}
          </div>
        )}

        {myItems.length === 0 ? (
          <p style={{textAlign: 'center', color: 'var(--text-secondary)'}}>У вас пока нет товаров на маркетплейсе</p>
        ) : (
          <div className="my-items-list" style={(activeOrders.length > 0 && !orderPopupDismissed) ? {marginBottom: '60px'} : undefined}>
            {myItems.map(item => {
              const isPromoted = item.promo_expires_at && new Date(item.promo_expires_at + 'Z').getTime() > Date.now();
              const isOutOfStock = item.stock_qty < item.unit_qty;
              return (
                <div key={item.id} className="my-item-row" style={isOutOfStock ? {borderColor: 'var(--danger)'} : (isPromoted ? {borderColor: item.promo_color} : undefined)}>
                  <div className="my-item-row-image" style={item.image_url ? {backgroundImage: `url(${item.image_url})`, backgroundSize: '90%', backgroundRepeat: 'no-repeat', backgroundPosition: 'center', imageRendering: 'pixelated'} : undefined}>
                    {!item.image_url && <span style={{fontSize: '24px'}}>📦</span>}
                    <span className="my-item-status" style={{color: item.status === 'ACTIVE' ? 'var(--success)' : 'var(--text-secondary)'}}>
                      {item.status === 'ACTIVE' ? 'Активен' : 'Скрыт'}
                    </span>
                  </div>

                  <div className="my-item-row-info">
                    <h4 className="my-item-title">{item.name}</h4>
                    <div className="my-item-meta" style={isOutOfStock ? {color: 'var(--danger)', fontWeight: 'bold'} : undefined}>
                      {isOutOfStock ? 'Товар закончился — пополните остаток' : `Остаток: ${item.stock_qty} шт. (лот ${item.unit_qty})`}
                    </div>
                    {!isOutOfStock && <div className="my-item-meta">Продано лотов: {item.purchases_count || 0}</div>}

                    {isPromoted && (
                      <div style={{display: 'flex', alignItems: 'center', gap: '4px', margin: '3px 0 0 0'}}>
                        <span style={{width: '8px', height: '8px', borderRadius: '50%', background: item.promo_color, flexShrink: 0}} />
                        <span style={{fontSize: '10px', color: 'var(--text-secondary)'}}>
                          {item.promo_label ? `«${item.promo_label}» ` : ''}до {new Date(item.promo_expires_at + 'Z').toLocaleDateString('ru-RU', {timeZone: 'Europe/Moscow', day: '2-digit', month: '2-digit'})}
                        </span>
                      </div>
                    )}

                    {!isOutOfStock && (
                      <div className="my-item-price-block">
                        <span className="my-item-price-label">Цена:</span>
                        <div className="my-item-price-row">
                          <input
                            type="number"
                            value={priceEdits[item.id] !== undefined ? priceEdits[item.id] : item.price}
                            onChange={e => setPriceEdits(prev => ({...prev, [item.id]: e.target.value.replace(/[^0-9]/g, '')}))}
                          />
                          <button
                            className="secondary-btn"
                            disabled={priceSavingId === item.id}
                            onClick={() => handleSavePrice(item)}
                          >{priceSavingId === item.id ? '···' : '✓'}</button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="my-item-row-actions">
                    <button className="primary-btn my-item-btn" onClick={() => { setTopupModal({ item }); setTopupLots(''); setTopupError(''); }}>Пополнить</button>
                    {isOutOfStock ? (
                      <button className="secondary-btn my-item-btn" style={{color: 'var(--danger)'}} onClick={() => setDeleteItemConfirm(item)}>🗑 Удалить</button>
                    ) : (
                      <>
                        <button className="secondary-btn my-item-btn" onClick={() => openPromoteModal(item)}>🚀 Продвинуть</button>
                        <button className="secondary-btn my-item-btn" onClick={() => openDescModal(item)}>✏️ Описание</button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {showSellModal && (
          <div className="modal-overlay">
            <div className="modal" style={{maxWidth: '400px'}}>
              <h3 style={{marginTop: 0}}>Выставить товар на продажу</h3>
              <div className="account-num-notice">
                <span className="icon">⚠️</span>
                <span><b>Важно:</b> после подачи заявки отправьте товар на сервере на почту <b>Azon</b>. Заявка будет одобрена после того, как товар поступит на склад.</span>
              </div>

              <div className="search-container">
                <input
                  type="text"
                  placeholder="Начните вводить название товара..."
                  value={sellName}
                  onChange={e => { setSellName(e.target.value); setSelectedCatalogItem(null); }}
                  onFocus={() => setSellNameFocus(true)}
                  onBlur={() => setTimeout(() => setSellNameFocus(false), 200)}
                />
                {sellNameFocus && catalogSuggestions.length > 0 && (
                  <div className="search-dropdown">
                    {catalogSuggestions.map(it => (
                      <div
                        key={it.id}
                        className="search-item"
                        style={{display: 'flex', alignItems: 'center', gap: '8px'}}
                        onClick={() => { setSellName(it.name); setSelectedCatalogItem(it); setSellNameFocus(false); }}
                      >
                        {it.image
                          ? <img src={it.image} alt="" style={{width: '24px', height: '24px', imageRendering: 'pixelated', flexShrink: 0}} />
                          : <span style={{width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0}}>📦</span>}
                        <span>{it.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {selectedCatalogItem && (
                <div style={{display: 'flex', alignItems: 'center', gap: '10px', background: 'var(--bg-subtle)', borderRadius: '10px', padding: '8px 10px', marginTop: '8px'}}>
                  <div style={{width: '40px', height: '40px', background: 'var(--accent-soft)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0}}>
                    {selectedCatalogItem.image
                      ? <img src={selectedCatalogItem.image} alt="" style={{width: '28px', height: '28px', imageRendering: 'pixelated'}} />
                      : <span style={{fontSize: '20px'}}>📦</span>}
                  </div>
                  <div>
                    <div style={{fontSize: '13px', fontWeight: 'bold'}}>{selectedCatalogItem.name}</div>
                    <div style={{fontSize: '11px', color: 'var(--text-secondary)'}}>Категория: {selectedCatalogItem.category}</div>
                  </div>
                </div>
              )}

              <label style={{fontSize: '12px', color: 'var(--text-secondary)', marginTop: '10px', display: 'block'}}>Количество товара в одном лоте продажи (шт.):</label>
              <input type="number" placeholder="Например, 64" value={sellUnitQty} onChange={e => setSellUnitQty(e.target.value.replace(/[^0-9]/g, ''))} />

              <label style={{fontSize: '12px', color: 'var(--text-secondary)'}}>Сколько лотов вы отправите:</label>
              <input type="number" placeholder="Например, 5" value={sellLots} onChange={e => setSellLots(e.target.value.replace(/[^0-9]/g, ''))} />

              {sellLots && sellUnitQty && (
                <p style={{fontSize: '12px', color: 'var(--accent-dark)', margin: '4px 0 0 0'}}>
                  Итого товара к отправке: <b>{(parseInt(sellLots, 10) || 0) * (parseInt(sellUnitQty, 10) || 0)} шт.</b>
                </p>
              )}

              <label style={{fontSize: '12px', color: 'var(--text-secondary)', marginTop: '10px', display: 'block'}}>Цена за один лот (сц):</label>
              <input type="number" placeholder="Например, 10" value={sellPrice} onChange={e => setSellPrice(e.target.value.replace(/[^0-9]/g, ''))} />

              <textarea placeholder="Описание товара (необязательно)" value={sellDesc} onChange={e => setSellDesc(e.target.value)} style={{height: '60px', resize: 'none'}} />

              {sellError && <p style={{color: 'var(--danger)', fontSize: '14px', textAlign: 'center', margin: '10px 0 0 0'}}>{sellError}</p>}

              <div className="modal-actions">
                <button className="primary-btn" disabled={isSelling} onClick={handleSellSubmit}>{isSelling ? 'Отправка...' : 'Подать заявку'}</button>
                <button className="secondary-btn" disabled={isSelling} onClick={() => { setShowSellModal(false); resetSellForm(); }}>Отмена</button>
              </div>
            </div>
          </div>
        )}

        {topupModal && (
          <div className="modal-overlay">
            <div className="modal" style={{maxWidth: '400px'}}>
              <h3 style={{marginTop: 0}}>Пополнение товара</h3>
              <div className="account-num-notice">
                <span className="icon">⚠️</span>
                <span>Товар: <b>{topupModal.item.name}</b><br/>
                <b>Важно:</b> отправьте товар на сервере на почту <b>Azon</b>. После поступления на склад остаток будет пополнен.</span>
              </div>

              <label style={{fontSize: '12px', color: 'var(--text-secondary)'}}>Сколько лотов вы отправите ({topupModal.item.unit_qty} шт./лот):</label>
              <input type="number" placeholder="Например, 3" value={topupLots} onChange={e => setTopupLots(e.target.value.replace(/[^0-9]/g, ''))} />

              {topupLots && (
                <p style={{fontSize: '12px', color: 'var(--accent-dark)', margin: '4px 0 0 0'}}>
                  Итого товара к отправке: <b>{(parseInt(topupLots, 10) || 0) * topupModal.item.unit_qty} шт.</b>
                </p>
              )}

              {topupError && <p style={{color: 'var(--danger)', fontSize: '14px', textAlign: 'center', margin: '10px 0 0 0'}}>{topupError}</p>}

              <div className="modal-actions">
                <button className="primary-btn" disabled={isTopupSubmitting} onClick={handleTopupSubmit}>{isTopupSubmitting ? 'Отправка...' : 'Подать заявку'}</button>
                <button className="secondary-btn" disabled={isTopupSubmitting} onClick={() => setTopupModal(null)}>Отмена</button>
              </div>
            </div>
          </div>
        )}

        {deleteItemConfirm && (
          <div className="modal-overlay">
            <div className="modal">
              <h3 style={{marginTop: 0}}>Удалить товар?</h3>
              <p style={{fontSize: '14px', color: 'var(--text-secondary)'}}>
                Товар «{deleteItemConfirm.name}» будет полностью снят с продажи. Это действие необратимо.
              </p>
              <div className="modal-actions">
                <button className="primary-btn" style={{background: 'var(--danger)'}} disabled={isDeletingMyItem} onClick={() => handleDeleteItem(deleteItemConfirm)}>{isDeletingMyItem ? 'Удаление...' : 'Удалить'}</button>
                <button className="secondary-btn" disabled={isDeletingMyItem} onClick={() => setDeleteItemConfirm(null)}>Отмена</button>
              </div>
            </div>
          </div>
        )}

        {promoteModal && (
          <div className="modal-overlay">
            <div className="modal" style={{maxWidth: '400px'}}>
              <h3 style={{marginTop: 0}}>Продвинуть товар</h3>
              <p style={{fontSize: '13px', color: 'var(--text-secondary)', marginTop: 0, marginBottom: '15px'}}>
                Товар: <b>{promoteModal.item.name}</b><br/>
                Продвинутый товар будет сразу привлекать внимание и показываться выше конкурентов.
              </p>

              <label style={{fontSize: '12px', color: 'var(--text-secondary)'}}>Количество дней:</label>
              <input type="number" placeholder="Например, 3" value={promoteDays} onChange={e => setPromoteDays(e.target.value.replace(/[^0-9]/g, ''))} />
              <p style={{fontSize: '11px', color: 'var(--text-secondary)', margin: '2px 0 10px 0'}}>{promotePricing.price_per_day} сц / день</p>

              <label style={{fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px'}}>Цвет выделения:</label>
              <div className="color-picker" style={{margin: '0 0 15px 0'}}>
                {PROMO_COLORS.map(c => (
                  <div
                    key={c}
                    className={`color-circle ${promoteColor === c ? 'selected' : ''}`}
                    style={{background: c}}
                    onClick={() => setPromoteColor(c)}
                  />
                ))}
              </div>

              <label style={{fontSize: '12px', color: 'var(--text-secondary)'}}>Ярлык с текстом (необязательно, +{promotePricing.label_price} сц/день):</label>
              <input type="text" placeholder="Например, ХИТ" maxLength={15} value={promoteLabel} onChange={e => setPromoteLabel(e.target.value)} />

              {promoteModal.item && (
                <div style={{display: 'flex', alignItems: 'center', gap: '8px', margin: '10px 0'}}>
                  <span style={{fontSize: '11px', color: 'var(--text-secondary)'}}>Превью:</span>
                  <div style={{position: 'relative', width: '44px', height: '44px', borderRadius: '10px', border: `2px solid ${promoteColor}`, background: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    {promoteModal.item.image_url
                      ? <img src={promoteModal.item.image_url} alt="" style={{width: '30px', height: '30px', imageRendering: 'pixelated'}} />
                      : <span style={{fontSize: '20px'}}>📦</span>}
                    {promoteLabel.trim() && (
                      <span style={{position: 'absolute', top: '-10px', left: '-6px', background: promoteColor, color: '#fff', fontSize: '10px', fontWeight: 'bold', padding: '3px 6px', borderRadius: '5px', whiteSpace: 'nowrap', lineHeight: 1.3}}>
                        {promoteLabel.trim()}
                      </span>
                    )}
                  </div>
                </div>
              )}

              <p style={{fontSize: '15px', fontWeight: 'bold', textAlign: 'center', margin: '10px 0'}}>Итого: {promoteTotalPrice} сц</p>

              {promoteError && <p style={{color: 'var(--danger)', fontSize: '14px', textAlign: 'center', margin: '0 0 10px 0'}}>{promoteError}</p>}

              <div className="modal-actions">
                <button className="primary-btn" disabled={isPromoting} onClick={handlePromoteSubmit}>{isPromoting ? 'Покупка...' : 'Продвинуть'}</button>
                <button className="secondary-btn" disabled={isPromoting} onClick={() => setPromoteModal(null)}>Отмена</button>
              </div>
            </div>
          </div>
        )}

        {descModal && (
          <div className="modal-overlay">
            <div className="modal" style={{maxWidth: '400px'}}>
              <h3 style={{marginTop: 0}}>Описание товара</h3>
              <p style={{fontSize: '13px', color: 'var(--text-secondary)', marginTop: 0, marginBottom: '10px'}}>
                Товар: <b>{descModal.item.name}</b>
              </p>
              <textarea
                placeholder="Описание товара (необязательно)"
                value={descText}
                onChange={e => setDescText(e.target.value.slice(0, 300))}
                style={{height: '100px', resize: 'none'}}
              />
              <p style={{fontSize: '11px', color: 'var(--text-secondary)', textAlign: 'right', margin: '4px 0 0 0'}}>{descText.length}/300</p>

              {descError && <p style={{color: 'var(--danger)', fontSize: '14px', textAlign: 'center', margin: '10px 0 0 0'}}>{descError}</p>}

              <div className="modal-actions">
                <button className="primary-btn" disabled={isSavingDesc} onClick={handleSaveDescription}>{isSavingDesc ? 'Сохранение...' : 'Сохранить'}</button>
                <button className="secondary-btn" disabled={isSavingDesc} onClick={() => setDescModal(null)}>Отмена</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (showCart) {
    return (
      <div className="market-page">
        {toast && <div className={`toast ${toast.isError ? 'error' : ''}`}>{toast.msg}</div>}
        {renderOrderPopup()}
        <button className="secondary-btn" onClick={() => setShowCart(false)} style={{width: 'auto', padding: '8px 15px', marginBottom: '15px'}}>← Назад</button>

        <h3 style={{margin: '0 0 15px 0'}}>Корзина</h3>

        {cartItems.length === 0 ? (
          <p style={{textAlign: 'center', color: 'var(--text-secondary)'}}>Корзина пуста</p>
        ) : (
          <>
            {cartItems.map(ci => (
              <div key={ci.item_id} className="card" style={{display: 'flex', gap: '12px', alignItems: 'center'}}>
                <div style={{width: '48px', height: '48px', flexShrink: 0, borderRadius: '10px', background: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', ...(ci.image_url ? {backgroundImage: `url(${ci.image_url})`, backgroundSize: '90%', backgroundRepeat: 'no-repeat', backgroundPosition: 'center', imageRendering: 'pixelated'} : {})}}>
                  {!ci.image_url && <span style={{fontSize: '22px'}}>📦</span>}
                </div>
                <div style={{flex: 1, minWidth: 0}}>
                  <p style={{margin: '0 0 2px 0', fontSize: '14px', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{ci.name}</p>
                  <p style={{margin: '0 0 4px 0', fontSize: '11px', color: 'var(--text-secondary)'}}>от {ci.seller || 'System'} · {ci.price} сц/лот</p>
                  <div className="cart-stepper" style={{position: 'static', display: 'inline-flex', background: 'var(--bg-subtle)'}}>
                    <button style={{background: 'var(--border)', color: 'var(--text-primary)'}} disabled={cartBusyId === ci.item_id} onClick={() => stepCart(ci, -1)}>−</button>
                    <span style={{color: 'var(--text-primary)'}}>{ci.lots}</span>
                    <button style={{background: 'var(--border)', color: 'var(--text-primary)'}} disabled={cartBusyId === ci.item_id || ci.lots >= ci.max_lots} onClick={() => stepCart(ci, 1, ci.max_lots)}>+</button>
                  </div>
                </div>
                <div style={{textAlign: 'right', flexShrink: 0}}>
                  <p style={{margin: '0 0 6px 0', fontWeight: 'bold', color: 'var(--accent-dark)'}}>{ci.line_total} сц</p>
                  <button className="link-btn" style={{color: 'var(--danger)', fontSize: '12px'}} onClick={() => handleCartSet(ci, 0)}>Удалить</button>
                </div>
              </div>
            ))}

            <div className="card" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <span style={{fontSize: '15px', fontWeight: 'bold'}}>Итого: {cartTotalLots} лот.</span>
              <span style={{fontSize: '18px', fontWeight: 800, color: 'var(--accent-dark)'}}>{cartTotalPrice} сц</span>
            </div>

            <button className="primary-btn" disabled={isCheckingOut} onClick={handleCheckout} style={(activeOrders.length > 0 && !orderPopupDismissed) ? {marginBottom: '60px'} : undefined}>{isCheckingOut ? 'Оформление...' : 'Оформить заказ'}</button>
          </>
        )}
      </div>
    );
  }

  if (showAdInfo) {
    return (
      <div className="market-page">
        {toast && <div className={`toast ${toast.isError ? 'error' : ''}`}>{toast.msg}</div>}
        {renderOrderPopup()}
        <button className="secondary-btn" onClick={() => setShowAdInfo(false)} style={{width: 'auto', padding: '8px 15px', marginBottom: '15px'}}>← Назад</button>
        <div className="card selectable-text">
          <h2 style={{marginTop: 0, color: 'var(--accent-dark)', fontSize: '22px'}}>Реклама в Azon Маркете</h2>
          <h4 style={{color: 'var(--warning)', marginTop: 0}}>Привлеки внимание всей аудитории сервера!</h4>
          
          <div style={{marginBottom: '15px'}}>
            <b style={{fontSize: '15px'}}>Ваше объявление видят все</b>
            <p style={{fontSize: '13px', color: 'var(--text-secondary)', margin: '4px 0 0 0'}}>Баннер на главной странице — 100% охват всех активных игроков. Никакие сообщения в чатах не дадут такой аудитории.</p>
          </div>
          
          <div style={{marginBottom: '15px'}}>
            <b style={{fontSize: '15px'}}>Работает 24/7 без выходных</b>
            <p style={{fontSize: '13px', color: 'var(--text-secondary)', margin: '4px 0 0 0'}}>В отличие от постов в чатах, которые улетают в историю за минуты, баннер висит на главной постоянно и привлекает внимание в любое время суток.</p>
          </div>
          
          <div style={{marginBottom: '15px'}}>
            <b style={{fontSize: '15px'}}>Подходит для любых целей</b>
            <p style={{fontSize: '13px', color: 'var(--text-secondary)', margin: '4px 0 0 0'}}>Товары, услуги, набор в город, ивенты, конкурсы, розыгрыши, объявления, поиск друзей — рекламируйте всё.</p>
          </div>
          
          <div style={{marginBottom: '20px'}}>
            <b style={{fontSize: '15px'}}>Выделяется визуально</b>
            <p style={{fontSize: '13px', color: 'var(--text-secondary)', margin: '4px 0 0 0'}}>Яркий цветной баннер с кнопкой привлекает внимание лучше, чем сотни однотипных сообщений в наборах.</p>
          </div>

          <h3 style={{marginBottom: '10px'}}>Как выглядит реклама?</h3>
          <div className="ad-banner-card" style={{background: 'linear-gradient(135deg, #26c6da, var(--accent-dark))', marginBottom: '15px'}}>
            <div className="ad-badge">Реклама</div>
            <h4>Ваш Заголовок</h4>
            <p>Краткое и цепляющее описание вашего предложения (до 80 символов).</p>
            <div className="ad-bottom">
              <button className="ad-btn">Ваша кнопка</button>
            </div>
            <div style={{textAlign: 'right', marginTop: '4px'}}>
              <span className="ad-author">от Вас</span>
            </div>
          </div>

          <div style={{background: 'var(--bg-subtle)', borderRadius: '12px', padding: '15px', marginBottom: '15px'}}>
            <h4 style={{marginTop: 0, marginBottom: '10px'}}>Стоимость и условия</h4>
            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px'}}>
              <span style={{color: 'var(--text-secondary)'}}>Стоимость</span>
              <b>{adPrice} сц / день</b>
            </div>
            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px'}}>
              <span style={{color: 'var(--text-secondary)'}}>Минимальный срок</span>
              <b>1 день</b>
            </div>
            <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '14px'}}>
              <span style={{color: 'var(--text-secondary)'}}>Оплата</span>
              <b>С банковского счёта Azon</b>
            </div>
          </div>

          <p style={{fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic', textAlign: 'center', marginBottom: '20px'}}>* Реклама запускается автоматически после оплаты.</p>

          <button className="primary-btn" onClick={async () => {
            try {
              const res = await fetch(`/api/bank/${tgId}`);
              const data = await res.json();
              if (!data.has_account) {
                showToastMsg('Откройте счет в банке', true);
                return;
              }
              fetchAdPrice();
              setShowOrderModal(true);
            } catch (e) {
              showToastMsg('Ошибка проверки счета', true);
            }
          }}>Заказать рекламу</button>
        </div>

        {showOrderModal && (
          <div className="modal-overlay">
            <div className="modal" style={{maxWidth: '400px'}}>
              <h3 style={{marginTop: 0}}>Создание объявления</h3>
              
              <input type="text" placeholder="Заголовок (до 30 симв.)" maxLength={30} value={adTitle} onChange={e => setAdTitle(e.target.value)} />
              <textarea placeholder="Описание (до 80 симв.)" maxLength={80} value={adDesc} onChange={e => setAdDesc(e.target.value)} style={{height: '60px', resize: 'none'}} />
              <div style={{display: 'flex', gap: '10px'}}>
                <input type="text" placeholder="Текст кнопки (до 15)" maxLength={15} value={adBtn} onChange={e => setAdBtn(e.target.value)} style={{flex: 1}} />
                <input type="text" placeholder="Ссылка (t.me/...)" value={adLink} onChange={e => setAdLink(e.target.value)} style={{flex: 1}} />
              </div>
              
              <label style={{fontSize: '14px', fontWeight: 'bold', display: 'block', marginTop: '10px'}}>Фоновое изображение:</label>
              <input type="file" accept="image/*" onChange={handleImageUpload} style={{fontSize: '14px', padding: '8px'}} />
              
              {adImage && (
                <div style={{marginTop: '10px'}}>
                  <p style={{fontSize: '12px', color: 'var(--text-secondary)', margin: '0 0 5px 0'}}>Потяните картинку, чтобы выбрать ракурс:</p>
                  <div 
                    ref={previewRef}
                    className="ad-banner-card"
                    style={{ 
                      backgroundImage: `url(${adImage})`, 
                      backgroundSize: `${bgSize}%`, 
                      backgroundPosition: `${bgPos.x}% ${bgPos.y}%`,
                      cursor: isDragging ? 'grabbing' : 'grab',
                      marginBottom: '15px',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      touchAction: 'none'
                    }}
                    onMouseDown={handleDragStart}
                    onMouseMove={handleDragMove}
                    onMouseUp={handleDragEnd}
                    onMouseLeave={handleDragEnd}
                    onTouchStart={handleDragStart}
                    onTouchMove={handleDragMove}
                    onTouchEnd={handleDragEnd}
                    onDragStart={(e) => e.preventDefault()}
                  >
                    <div className="ad-overlay"></div>
                    <div className="ad-badge">Реклама</div>
                    <h4 style={{position: 'relative', zIndex: 2}}>{adTitle || 'Заголовок'}</h4>
                    <p style={{position: 'relative', zIndex: 2}}>{adDesc || 'Описание вашего предложения'}</p>
                    <div className="ad-bottom" style={{position: 'relative', zIndex: 2}}>
                      <button className="ad-btn">{adBtn || 'Кнопка'}</button>
                    </div>
                    <div style={{position: 'relative', zIndex: 2, textAlign: 'right', marginTop: '4px'}}>
                      <span className="ad-author">от Вас</span>
                    </div>
                  </div>
                </div>
              )}

              <div style={{marginTop: '10px'}}>
                <label style={{fontSize: '12px', color: 'var(--text-secondary)'}}>Количество суток рекламы:</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={adDays}
                  onChange={e => setAdDays(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="Например, 3"
                />
              </div>

              <div style={{background: 'var(--bg-subtle)', padding: '10px', borderRadius: '8px', marginTop: '10px', textAlign: 'center'}}>
                <span style={{fontSize: '14px', color: 'var(--text-secondary)'}}>К оплате: </span>
                <b style={{fontSize: '18px', color: 'var(--accent-dark)'}}>{calculateDays() * adPrice} сц</b>
              </div>

              {orderError && <p style={{color: 'var(--danger)', fontSize: '14px', textAlign: 'center', margin: '10px 0 0 0'}}>{orderError}</p>}

              <div className="modal-actions">
                <button className="primary-btn" disabled={isOrdering} onClick={handleOrderSubmit}>{isOrdering ? 'Оплата...' : 'Оплатить'}</button>
                <button className="secondary-btn" disabled={isOrdering} onClick={() => setShowOrderModal(false)}>Отмена</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!initialLoadDone) {
    return <div className="card" style={{textAlign: 'center', color: 'var(--text-secondary)'}}>Загрузка...</div>;
  }

  return (
    <div className="market-page">
      {toast && <div className={`toast ${toast.isError ? 'error' : ''}`}>{toast.msg}</div>}
      {renderOrderPopup()}

      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px' }}>
        <div className="search-container market-search-wrapper" style={{ flex: 1, marginBottom: 0 }}>
          <span className="market-search-icon">🔍</span>
          <input
            type="text"
            placeholder="Искать на Azon Market"
            value={search}
            onChange={(e) => { setSearch(e.target.value); if (e.target.value.trim()) setActiveCategory(null); }}
            onFocus={() => setSearchFocus(true)}
            onBlur={() => setTimeout(() => setSearchFocus(false), 200)}
          />
          {searchFocus && searchSuggestions.length > 0 && (
            <div className="search-dropdown">
              {searchSuggestions.map(it => (
                <div
                  key={it.id}
                  className="search-item"
                  style={{display: 'flex', alignItems: 'center', gap: '8px'}}
                  onClick={() => { setSearch(it.name); setSearchFocus(false); setActiveCategory(null); }}
                >
                  {it.image
                    ? <img src={it.image} alt="" style={{width: '24px', height: '24px', imageRendering: 'pixelated', flexShrink: 0}} />
                    : <span style={{width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0}}>📦</span>}
                  <span>{it.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <button className="secondary-btn" style={{width: 'auto', flexShrink: 0, padding: '10px 14px', marginBottom: 0}} onClick={() => setShowMyItems(true)}>🛒 Мои товары</button>
      </div>

      {!search.trim() && (
        <>
          <AdCarousel onOrderClick={() => setShowAdInfo(true)} botUsername={botUsername} onItemLinkClick={openItemById} serverAds={serverAds} />

          <ScrollableRow>
            {categories.map((cat, idx) => (
              <div 
                key={idx} 
                className={`market-category-item ${activeCategory === cat.name ? 'active' : ''}`}
                onClick={() => toggleCategory(cat.name)}
              >
                <div className="market-category-icon">{cat.icon}</div>
                <span className="market-category-name">{cat.name}</span>
              </div>
            ))}
          </ScrollableRow>

          <div className="market-items-header">
            <h3>{activeCategory || 'Все товары'}</h3>
            <span>{totalItems !== null ? totalItems : items.length} товаров</span>
          </div>
        </>
      )}

      {loading ? (
        <p style={{textAlign: 'center', color: 'var(--text-secondary)'}}>Загрузка товаров...</p>
      ) : items.length === 0 ? (
        <p style={{textAlign: 'center', color: 'var(--text-secondary)'}}>Товары не найдены</p>
      ) : (
        <div className="market-items-grid" style={(localCartLots > 0 || (activeOrders.length > 0 && !orderPopupDismissed)) ? {marginBottom: '95px'} : undefined}>
          {items.map((item, idx) => {
            const inCartLots = cart[item.id] || 0;
            const isOwnItem = String(item.tg_id) === String(tgId);
            const outOfStock = item.stock_qty < item.unit_qty;
            const maxLots = Math.floor(item.stock_qty / item.unit_qty);
            // Ряды по 3 карточки — 5-й ряд (15-й элемент пачки) даёт индекс
            // items.length - 7 (0-based): именно на нём висит невидимый триггер
            // подгрузки следующей пачки, оставляя ещё 2 ряда (6 карточек) про запас
            // до реального конца списка — так подгрузка стартует заранее.
            // Math.max — страховка для коротких списков: если товаров меньше семи,
            // триггер вешаем на первую карточку, иначе индекс ушёл бы в минус
            // и подгрузка не запустилась бы вовсе.
            const isLoadMoreTrigger = hasMore && idx === Math.max(0, items.length - 7);
            return (
              <div
                key={item.id}
                ref={isLoadMoreTrigger ? observerTarget : null}
                className="market-item-card"
                style={item.promo_color ? {border: `2px solid ${item.promo_color}`} : undefined}
                onClick={() => setItemDetail(item)}
              >
                <div className="market-item-image-bg" style={item.image_url ? {backgroundImage: `url(${item.image_url})`, backgroundSize: '90%', backgroundRepeat: 'no-repeat', backgroundPosition: 'center', imageRendering: 'pixelated'} : undefined}>
                  {item.promo_label && (
                    <span style={{position: 'absolute', top: '4px', left: '4px', background: item.promo_color, color: getContrastTextColor(item.promo_color), fontSize: '10px', fontWeight: 'bold', padding: '3px 7px', borderRadius: '6px', zIndex: 1, lineHeight: 1.3}}>
                      {item.promo_label}
                    </span>
                  )}
                  {!item.image_url && <div className="market-item-pic">📦</div>}
                </div>
                <div className="market-item-info">
                  <h4 className="market-item-title">{item.name}</h4>
                  <span className="market-item-seller" style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                    {item.seller && <PlayerAvatar nickname={item.seller} size={14} rounded={3} />}
                    от {item.seller || 'System'}
                  </span>
                  <div style={{fontSize: '11px', color: 'var(--text-secondary)', margin: '2px 0'}}>{item.unit_qty} шт./лот</div>
                  <div className="market-item-price">{item.price} <span>сц</span></div>
                </div>

                {!isOwnItem && !outOfStock && (
                  inCartLots > 0 ? (
                    <div className="cart-bar cart-bar-stepper" onClick={e => e.stopPropagation()}>
                      <button disabled={cartBusyId === item.id} onClick={() => stepCart(item, -1)}>−</button>
                      <span>{inCartLots}</span>
                      <button disabled={cartBusyId === item.id || inCartLots >= maxLots} onClick={() => stepCart(item, 1, maxLots)}>+</button>
                    </div>
                  ) : (
                    <button
                      className="cart-bar cart-bar-add"
                      disabled={cartBusyId === item.id}
                      onClick={e => { e.stopPropagation(); handleCartSet(item, 1); }}
                    >+ В корзину</button>
                  )
                )}
              </div>
            );
          })}
        </div>
      )}

      {items.length > 0 && (
        <div style={{ height: '20px', margin: '10px 0', display: 'flex', justifyContent: 'center' }}>
          {hasMore && <span style={{color: 'var(--text-secondary)', fontSize: '14px'}}>Загрузка...</span>}
        </div>
      )}

      {localCartLots > 0 && (
        <button className="floating-cart-btn" onClick={() => setShowCart(true)}>
          <svg width="50" height="50" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 4H5L5.6 7M5.6 7L7 15H18L20 7H5.6Z" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="9" cy="19" r="1.4" fill="#fff"/>
            <circle cx="17" cy="19" r="1.4" fill="#fff"/>
          </svg>
          <span className="floating-cart-badge">{localCartLots}</span>
        </button>
      )}

      {itemDetail && (
        <div className="modal-overlay" onClick={() => setItemDetail(null)}>
          <div className="modal" style={{width: '82%', maxWidth: '340px', position: 'relative'}} onClick={e => e.stopPropagation()}>
            {/* Раньше эти кнопки лежали поверх контента (position: absolute) и на длинных
                названиях товара визуально перекрывали первую строку заголовка — паддинг
                справа под них можно было подобрать неточно. Теперь это обычная строка в
                потоке документа над контентом — заголовок физически не может оказаться
                под кнопками, независимо от их количества или длины названия. */}
            <div style={{display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '10px'}}>
              <button
                onClick={() => handleShareItem(itemDetail)}
                style={{width: '26px', height: '26px', borderRadius: '50%', background: 'var(--bg-subtle)', color: 'var(--accent-dark)', border: 'none', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, cursor: 'pointer', flexShrink: 0}}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line></svg>
              </button>
              <button
                onClick={() => setItemDetail(null)}
                style={{width: '26px', height: '26px', borderRadius: '50%', background: 'var(--bg-subtle)', color: 'var(--text-secondary)', border: 'none', fontSize: '15px', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, cursor: 'pointer', flexShrink: 0}}
              >✕</button>
            </div>

            {itemDetail.promo_label && (
              <span style={{display: 'inline-block', background: itemDetail.promo_color, color: getContrastTextColor(itemDetail.promo_color), fontSize: '11px', fontWeight: 'bold', padding: '3px 8px', borderRadius: '6px', marginBottom: '10px'}}>
                {itemDetail.promo_label}
              </span>
            )}

            <div style={{display: 'flex', gap: '14px', alignItems: 'flex-start'}}>
              <div
                style={{
                  width: '90px', height: '90px', flexShrink: 0, borderRadius: '12px', background: 'var(--accent-soft)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  ...(itemDetail.image_url ? {backgroundImage: `url(${itemDetail.image_url})`, backgroundSize: '90%', backgroundRepeat: 'no-repeat', backgroundPosition: 'center', imageRendering: 'pixelated'} : {})
                }}
              >
                {!itemDetail.image_url && <span style={{fontSize: '36px'}}>📦</span>}
              </div>
              <div style={{flex: 1, minWidth: 0}}>
                <h3 style={{margin: '0 0 4px 0'}}>{itemDetail.name}</h3>
                <p style={{margin: '0 0 6px 0', fontSize: '12px', color: 'var(--text-secondary)'}}>{itemDetail.category}</p>
                <div style={{fontSize: '20px', fontWeight: 800, color: 'var(--accent-dark)'}}>{itemDetail.price} <span style={{fontSize: '13px', color: 'var(--border-strong)'}}>сц</span></div>
              </div>
            </div>

            <div style={{background: 'var(--bg-subtle)', borderRadius: '10px', padding: '10px 12px', margin: '14px 0'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', padding: '4px 0'}}>
                <span style={{color: 'var(--text-secondary)'}}>Продавец</span>
                <b style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                  {itemDetail.seller && <PlayerAvatar nickname={itemDetail.seller} size={20} rounded={4} />}
                  {itemDetail.seller || 'System'}
                </b>
              </div>
              <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '4px 0'}}>
                <span style={{color: 'var(--text-secondary)'}}>В одном лоте</span>
                <b>{itemDetail.unit_qty} шт.</b>
              </div>
              <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '4px 0'}}>
                <span style={{color: 'var(--text-secondary)'}}>В наличии</span>
                <b>{itemDetail.stock_qty} шт.</b>
              </div>
              <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '4px 0'}}>
                <span style={{color: 'var(--text-secondary)'}}>Продано лотов</span>
                <b>{itemDetail.purchases_count || 0}</b>
              </div>
            </div>

            <p style={{fontSize: '12px', fontWeight: 'bold', color: 'var(--text-secondary)', margin: '0 0 4px 0'}}>Описание</p>
            <p style={{fontSize: '14px', color: itemDetail.description ? 'var(--text-primary)' : 'var(--text-secondary)', fontStyle: itemDetail.description ? 'normal' : 'italic', margin: '0 0 15px 0', lineHeight: 1.4}}>
              {itemDetail.description || 'Продавец не оставил описание'}
            </p>

            {String(itemDetail.tg_id) !== String(tgId) && itemDetail.stock_qty >= itemDetail.unit_qty && (
              (cart[itemDetail.id] || 0) > 0 ? (
                <div className="cart-bar cart-bar-stepper" style={{borderRadius: '12px'}}>
                  <button disabled={cartBusyId === itemDetail.id} onClick={() => stepCart(itemDetail, -1)}>−</button>
                  <span>{cart[itemDetail.id]}</span>
                  <button disabled={cartBusyId === itemDetail.id || (cart[itemDetail.id] || 0) >= Math.floor(itemDetail.stock_qty / itemDetail.unit_qty)} onClick={() => stepCart(itemDetail, 1, Math.floor(itemDetail.stock_qty / itemDetail.unit_qty))}>+</button>
                </div>
              ) : (
                <button
                  className="cart-bar cart-bar-add"
                  style={{borderRadius: '12px'}}
                  disabled={cartBusyId === itemDetail.id}
                  onClick={() => handleCartSet(itemDetail, 1)}
                >+ В корзину</button>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Market;